import axios from 'axios';
import { getOrCreateFingerprint } from '../utils/fingerprint';
import API_BASE_URL from '../config/api';

// Create axios instance with sensible defaults and timeouts
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  },
  // Enable cross-site credentials (cookies) for authentication
  withCredentials: true,
  // reasonable request timeout
  timeout: 15000
});

// Simple exponential backoff retry for transient network/server errors
api.interceptors.response.use(undefined, async (error) => {
  const config = error?.config;
  if (!config) return Promise.reject(error);

  // Do not retry if explicitly disabled
  if (config.__noRetry) return Promise.reject(error);

  config.__retryCount = config.__retryCount || 0;
  const MAX_RETRIES = 3;
  const method = String(config.method || 'get').toLowerCase();
  const isIdempotent = ['get', 'head', 'options'].includes(method);

  // Do not retry unsafe methods or explicit 429 responses.
  if (!isIdempotent || error.response?.status === 429) {
    return Promise.reject(error);
  }

  // Retry for network errors, timeouts, or server errors (5xx).
  const shouldRetry = !error.response || (error.response && error.response.status >= 500) || error.code === 'ECONNABORTED';
  if (shouldRetry && config.__retryCount < MAX_RETRIES) {
    config.__retryCount += 1;
    const delay = Math.min(10000, Math.pow(2, config.__retryCount) * 300 + Math.random() * 200);
    await new Promise((res) => setTimeout(res, delay));
    return api(config);
  }

  return Promise.reject(error);
});

// Attach fingerprint and auth token to all requests (reads token from localStorage)
// Also add cache-busting headers to prevent browser from caching responses
api.interceptors.request.use((config) => {
  try {
    if (typeof FormData !== 'undefined' && config.data instanceof FormData && config.headers) {
      config.headers.set('Content-Type', 'multipart/form-data');
    }

    const fp = getOrCreateFingerprint();
    if (fp && config.headers) config.headers['x-fingerprint'] = fp;
    
    // Add cache-busting headers to prevent browser caching of user-specific data
    // This ensures every request is fresh, especially for school-work/staff/user endpoints
    config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
    config.headers['Pragma'] = 'no-cache';
    config.headers['Expires'] = '0';
    
    // Determine which auth token to use
    // admin_auth_token is used for admin routes
    // staff_auth_token is used for staff routes
    // auth_token is used for public/user routes
    const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_auth_token') : null;
    const staffToken = typeof window !== 'undefined' ? localStorage.getItem('staff_auth_token') : null;
    const schoolToken = typeof window !== 'undefined' ? localStorage.getItem('school_token') : null;
    const publicToken = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;

    const requestUrl = String(config.url || '').toLowerCase();
    const strippedUrl = requestUrl.replace(/^https?:\/\/[^/]+/, '');
    const normalizedUrl = strippedUrl.replace(/^\/api/, '').split('?')[0];
    const schoolRoute = /^\/schools(\/|$)/;
    const schoolPaymentRoute = /^\/payments(\/|$)/;
    const schoolStaffRoute = /^\/staff\/[^/]+\/(staff|role)(\/|$)|^\/staff\/[^/]+$/;
    const staffRoute = /^\/staff(\/|$)/;

    const authHeaderAlreadySet = Boolean(
      config.headers && (config.headers['x-auth-token'] || config.headers['Authorization'])
    );
    const isDiscussionGuestLogin = config.headers?.['x-discussion-guest-login'] === 'true';

    const parentTicketSchoolRoute = /^\/parent-tickets\/school(\/|$)/;
    const token = parentTicketSchoolRoute.test(normalizedUrl)
      ? (schoolToken || adminToken || staffToken || publicToken)
      : schoolRoute.test(normalizedUrl)
        ? (schoolToken || adminToken || staffToken || publicToken)
        : schoolPaymentRoute.test(normalizedUrl)
          ? (schoolToken || adminToken || staffToken || publicToken)
          : schoolStaffRoute.test(normalizedUrl)
            ? (adminToken || staffToken || publicToken)
            : staffRoute.test(normalizedUrl)
              ? (staffToken || publicToken || adminToken)
              : (publicToken || staffToken || adminToken || schoolToken);

    if (!isDiscussionGuestLogin && !authHeaderAlreadySet && token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
      config.headers['x-auth-token'] = token;
    }

    if (process.env.NODE_ENV !== 'production') {
      const tokenSummary = token ? `${token.slice(0,6)}...${token.slice(-6)}` : 'none';
      const tokenType = schoolRoute.test(normalizedUrl)
        ? 'school'
        : schoolStaffRoute.test(normalizedUrl)
          ? 'admin'
          : staffRoute.test(normalizedUrl)
            ? 'staff'
            : publicToken
              ? 'public'
              : staffToken
                ? 'staff'
                : adminToken
                  ? 'admin'
                  : 'none';
      // eslint-disable-next-line no-console
      console.debug('[api] request ->', config.method?.toUpperCase(), config.url, { tokenType, token: tokenSummary, fingerprint: fp });
    }
  } catch (e) {
    // ignore header attach errors
  }
  return config;
}, (err) => Promise.reject(err));

// Response logging in development to aid debugging
api.interceptors.response.use((res) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.debug('[api] response <-', res.config?.method?.toUpperCase(), res.config?.url, res.status, res.data);
  }
  return res;
}, (err) => {
  if (err?.response?.data?.code === 'SESSION_REPLACED' && typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth:session-replaced'));
  }
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.error('[api] response error <-', err?.config?.method?.toUpperCase(), err?.config?.url, err?.response?.status, err?.response?.data || err.message);
  }
  return Promise.reject(err);
});

export default api;
